const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CANCEL = Symbol("cancel");
export const PAUSED = Symbol("paused");

const ttsCache = new Map();

export const fetchTTS = async (text) => {
  if (ttsCache.has(text)) return ttsCache.get(text);
  const p = fetch(`${API}/tts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: text.slice(0, 4000) }) })
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("tts"))))
    .then((b) => URL.createObjectURL(b));
  ttsCache.set(text, p);
  p.catch(() => ttsCache.delete(text));
  return p;
};

export const transcribe = async (blob) => {
  const form = new FormData();
  form.append("audio", blob, blob.type.includes("mp4") ? "voz.mp4" : "voz.webm");
  const res = await fetch(`${API}/transcribe`, { method: "POST", body: form });
  if (!res.ok) return "";
  return (await res.json()).text || "";
};

let ctx;
export const beep = (freq = 880, ms = 160) => {
  try {
    ctx ||= new AudioContext();
    if (ctx.state === "suspended") ctx.resume();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.15, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + ms / 1000);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + ms / 1000);
  } catch {
    /* audio not available */
  }
};

// Records until the user stops talking; resolves a Blob, null (no speech) or PAUSED.
export const recordUtterance = (stream, abortRef, { waitMs = 7000, silenceMs = 1500, maxMs = 25000, startSpoken = false } = {}) =>
  new Promise((resolve) => {
    ctx ||= new AudioContext();
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 1024;
    src.connect(an);
    const data = new Uint8Array(an.fftSize);
    const mime = ["audio/webm", "audio/mp4"].find((m) => MediaRecorder.isTypeSupported(m)) || "";
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    let spoke = startSpoken;
    let aborted = false;
    let lastVoice = performance.now();
    const t0 = performance.now();
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      clearInterval(iv);
      src.disconnect();
      if (aborted) return resolve(PAUSED);
      resolve(spoke ? new Blob(chunks, { type: rec.mimeType || "audio/webm" }) : null);
    };
    const iv = setInterval(() => {
      an.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += ((data[i] - 128) / 128) ** 2;
      const rms = Math.sqrt(sum / data.length);
      const now = performance.now();
      if (rms > 0.035) { spoke = true; lastVoice = now; }
      const el = now - t0;
      if ((!spoke && el > waitMs) || (spoke && now - lastVoice > silenceMs) || el > maxMs) rec.state === "recording" && rec.stop();
    }, 100);
    abortRef.current = () => { aborted = true; rec.state === "recording" && rec.stop(); };
    rec.start();
  });

const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[.,!?¿¡]/g, " ").trim();

const COMMANDS = {
  exit: ["salir", "terminar", "adios", "exit", "quit", "goodbye", "sortir", "quitter", "au revoir"],
  next: ["siguiente", "sigue", "continua", "continuar", "adelante", "next", "continue", "go on", "suivant", "continuer"],
  repeat: ["repetir", "repite", "otra vez", "repeat", "again", "repeter", "repete", "encore"],
  nearby: ["oficinas cercanas", "cercanas", "cerca de mi", "mapa", "nearby", "nearby offices", "near me", "map", "bureaux proches", "proches", "carte"],
  help: ["ayuda", "help", "aide", "opciones", "options"],
  pause: ["pausa", "espera", "alto", "detente", "pause", "wait", "hold on", "attends", "attendez"],
  followup: ["seguimiento", "follow up", "follow-up", "followup", "suivi"],
};

const STRICT = new Set(["exit", "next", "repeat", "help", "pause"]);

export const parseCommand = (text) => {
  const n = norm(text).replace(/\s+/g, " ");
  const words = n.split(" ");
  if (words.length > 5) return null;
  const padded = ` ${n} `;
  return Object.keys(COMMANDS).find((k) => (!STRICT.has(k) || words.length <= 2) && COMMANDS[k].some((w) => padded.includes(` ${w} `))) || null;
};

export const splitSteps = (md) => md.split(/\n(?=##\s*\d\.)/).map((s) => s.trim()).filter(Boolean);

export const MIC = { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } };

// Resolves true when the user starts talking (sustained voice), false when aborted.
export const detectVoice = (stream, abortRef, { threshold = 0.08, frames = 5 } = {}) =>
  new Promise((resolve) => {
    ctx ||= new AudioContext();
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 1024;
    src.connect(an);
    const data = new Uint8Array(an.fftSize);
    let hits = 0;
    let fin = false;
    const done = (v) => { if (fin) return; fin = true; clearInterval(iv); src.disconnect(); resolve(v); };
    const iv = setInterval(() => {
      an.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += ((data[i] - 128) / 128) ** 2;
      hits = Math.sqrt(sum / data.length) > threshold ? hits + 1 : 0;
      if (hits >= frames) done(true);
    }, 50);
    abortRef.current = () => done(false);
  });

const words = (s) => norm(s).split(/\s+/).filter((w) => w.length > 2);
export const overlap = (said, spoken) => {
  const a = words(said);
  if (!a.length) return 1;
  const b = new Set(words(spoken));
  return a.filter((w) => b.has(w)).length / a.length;
};
